import classNames from 'classnames';
import React, {
  CSSProperties,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { GroupBase, MenuListProps, MultiValueProps } from 'react-select';
import { OptionProps, StylesConfig, components } from 'react-select';
import type {
  AsyncPaginateProps,
  ComponentProps,
  UseAsyncPaginateParams,
} from 'react-select-async-paginate';
import {
  AsyncPaginate,
  useComponents,
  withAsyncPaginate,
  wrapMenuList,
} from 'react-select-async-paginate';
import type { CreatableProps } from 'react-select/creatable';
import CreatableSelect from 'react-select/creatable';
import { CheckBlueIcon, CheckIcon, InfoCircleIcon, WarningYellowIcon } from '../icons';
import { TooltipNew } from './TooltipNew.tsx';

interface OptionType {
  avatar?: ReactNode;
  label: string;
  value: string;
}

function normalize(val: any): number | string {
  if (val && typeof val === 'object' && ('id' in val || 'toString' in val)) {
    return val?.id || val?.toString();
  }

  if (val && typeof val === 'object') {
    return val?.toString();
  }

  return val;
}

export type { StylesConfig } from 'react-select';

const MultiValueLabel = (props: MultiValueProps<OptionType>) => {
  return props.data.avatar ? (
    <components.MultiValueLabel {...props}>
      <div className="flex justify-between gap-2 items-center">
        {props.data.avatar}
        <div className="whitespace-nowrap max-w-[168px] overflow-hidden text-ellipsis">
          {props.children}
        </div>
      </div>
    </components.MultiValueLabel>
  ) : (
    <div
      style={{ padding: '7px 10px 7px 12px' }}
      className="whitespace-nowrap max-w-[168px] overflow-hidden text-ellipsis"
    >
      {props.children}
    </div>
  );
};

export const Option = ({ children, isSelected, ...props }: OptionProps) => {
  const showSelectedOptionsInMenu =
    (props.selectProps as any)?.showSelectedOptionsInMenu && props.isMulti;

  return (
    <components.Option
      isSelected={isSelected}
      className={classNames('text-[#6366F1]', props.className)}
      {...props}
    >
      {showSelectedOptionsInMenu ? (
        <span className="absolute left-3 flex h-5 w-5 items-center justify-center">
          {isSelected && <img src={CheckBlueIcon} alt="selected" className="h-7 w-7" />}
        </span>
      ) : (
        isSelected && (
          <span className="absolute flex items-center justify-center right-4">
            <img src={CheckIcon} alt="selected" />
          </span>
        )
      )}
      {children}
    </components.Option>
  );
};

export const styles = ({
  positionMenu = 'down',
  creatable = false,
  position = 'relative',
  sizeMulti = 'md',
  autoHeight = true,
  isClearable = false,
  isError = false,
  withBorder = true,
  showBorderBottom = true,
  disabled = false,
  option = {},
  rounded,
  bgTransparent,
  showSelectedOptionsInMenu = false,
}: {
  positionMenu?: 'up' | 'down';
  position?: 'relative' | 'absolute';
  creatable?: boolean;
  withBorder?: boolean;
  rounded?: 'full' | 'right' | 'left';
  sizeMulti?: 'sm' | 'md';
  autoHeight?: boolean;
  bgTransparent?: boolean;
  isClearable?: boolean;
  isError?: boolean;
  showBorderBottom?: boolean;
  disabled?: boolean;
  option?: CSSProperties;
  showSelectedOptionsInMenu?: boolean;
}): StylesConfig => {
  const styles: StylesConfig = {
    indicatorSeparator: () => ({}),
    clearIndicator: base => {
      return isClearable
        ? base
        : ({
            ...base,
            display: 'none',
          } as any);
    },
    menu: provider => {
      return {
        ...provider,
        top: positionMenu === 'down' ? '34px' : 'auto',
        bottom: positionMenu === 'down' ? 'auto' : '35px',
        // borderTopLeftRadius: '0',
        // borderTopRightRadius: '0',
        left: '1.5px',
        width: 'calc(100% - 4px)',
        borderRadius: '8px',
        maxHeight: 'auto',
        zIndex: 2,
      } as any;
    },
    container: provider => ({ ...provider, position } as any),
    option: (provider, { isFocused, isSelected, isMulti }) => {
      const showChecklist = showSelectedOptionsInMenu && isMulti;

      return {
        ...provider,
        wordBreak: 'break-word',
        backgroundColor: isFocused || (!showChecklist && isSelected) ? '#efefef' : '#fff',
        color: isSelected ? (showChecklist ? '#0369A1' : 'rgb(49, 105, 247)') : '#171717',
        cursor: isFocused ? 'pointer' : 'default',
        paddingLeft: showChecklist ? 40 : provider.paddingLeft,
        paddingRight: !showChecklist && isSelected ? 35 : 0,
        ...(showChecklist
          ? {
              ':active': {
                backgroundColor: 'rgba(3, 105, 161, 0.12)',
              },
            }
          : {}),
        ...option,
      } as any;
    },
    control: (provider, { isFocused }) => {
      return {
        ...provider,
        ['&:hover']: {
          borderColor: withBorder ? '#111' : '',
          cursor: creatable ? 'text' : 'pointer',
        },
        backgroundColor: bgTransparent ? '' : provider?.backgroundColor,
        paddingBottom: showBorderBottom && !withBorder ? '4px' : '',
        border: !withBorder ? 'none' : undefined,
        borderColor: withBorder
          ? `${isError ? undefined : isFocused ? '#171717' : 'rgba(23, 23, 23, .16)'}`
          : undefined,
        boxShadow: '',
        borderWidth: showBorderBottom ? 1 : 0,
        borderBottom: showBorderBottom
          ? `solid 1px ${
              isError ? 'rgb(220 38 38)' : isFocused ? '#171717' : 'rgba(23, 23, 23, .16)'
            }`
          : undefined,
        borderRadius:
          !rounded || !withBorder
            ? '0px'
            : rounded === 'full'
            ? '8px'
            : rounded === 'right'
            ? '0px 8px 8px 0px'
            : '8px 0px 0px 8px',
      } as any;
    },
    valueContainer: base => {
      return {
        ...base,
        padding: withBorder ? '0px 0px 0px 8px' : '0px',
        overflow: !autoHeight ? 'auto' : 'hidden',
        fontSize: '14px',
        lineHeight: '20px',
        fontWeight: '400',
      } as any;
    },
    menuList: base => ({
      ...base,
      overscrollBehavior: 'contain',
    }),
    placeholder: base => {
      return {
        ...base,
        color: '#A3A3A3',
      } as any;
    },
    singleValue: base => ({
      ...base,
      fontSize: '14px',
      color: '#171717',
      lineHeight: '22px',
    }),
    multiValue: (base, props: any) => ({
      ...base,
      backgroundColor: '#E5E7EB',
      borderRadius: '100px',
      height: '26px',
      maxWidth: '168px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      whiteSpace: 'nowrap',
      fontSize: '14px',
      color: '#171717',
      lineHeight: '22px',
      border: '1px solid #E5E7EB',
      padding: props?.data?.avatar
        ? '0px'
        : sizeMulti === 'md'
        ? '7px 0px 7px 12px'
        : '6px 0px 6px 8px',
    }),
    multiValueLabel: base => {
      return {
        ...base,
        padding: '0',
        paddingLeft: '0',
        paddingRight: '6px',
        fontSize: '14px',
        lineHeight: '22px',
        color: '#4B5563',
      } as any;
    },
    multiValueRemove: base => {
      return {
        ...base,
        display: disabled ? 'none' : 'initial',
        marginLeft: 'auto',
        borderRadius: '21px 21px 21px 21px',
        ['&:hover']: {
          backgroundColor: '#ffdede',
        },
      } as any;
    },
    input: (base, { isMulti }) => {
      return {
        ...base,
        margin: isMulti ? '0' : '2px',
      } as any;
    },
  };
  return styles;
};

type AsyncPaginateCreatableProps<
  OptionType,
  Group extends GroupBase<OptionType>,
  Additional,
  IsMulti extends boolean,
> = CreatableProps<OptionType, IsMulti, Group> &
  UseAsyncPaginateParams<OptionType, Group, Additional> &
  ComponentProps<OptionType, Group, IsMulti>;

type AsyncPaginateCreatableType = <
  OptionType,
  Group extends GroupBase<OptionType>,
  Additional,
  IsMulti extends boolean = false,
>(
  props: AsyncPaginateCreatableProps<OptionType, Group, Additional, IsMulti>,
) => ReactElement;

const CreatableAsyncPaginate = withAsyncPaginate(CreatableSelect) as AsyncPaginateCreatableType;

export interface SelectPaginateOption<T> {
  readonly label: string | React.ReactNode | JSX.Element;
  readonly value: T;
  readonly avatar?: ReactNode;
}

export const createOption = (label: string): SelectPaginateOption<string> => ({
  label,
  value: label,
});

export type OptionValue<IsMulti, Option> = IsMulti extends false
  ? SelectPaginateOption<Option>
  : SelectPaginateOption<Option>[];

interface AddedLastOption {
  label: string | React.ReactNode | JSX.Element;
  onClick?: () => void;
}

export interface CreatableSelectProps<Option = any, IsMulti extends boolean = false> {
  cacheUniqs?: unknown[];
  name?: string;
  id?: string;
  isLoading?: boolean;
  value?: OptionValue<IsMulti, Option> | null;
  onChange?: (newValue: OptionValue<IsMulti, Option>) => void;
  onAsync: (page: number, search?: string) => Promise<OptionValue<true, Option>>;
  defaultValue?: OptionValue<IsMulti, Option> | null;
  label?: string | ReactNode;
  className?: string;
  withCreatable?: boolean;
  error?: string;
  warning?: string;
  positionMenu?: 'up' | 'down';
  position?: 'relative' | 'absolute';
  disabled?: boolean;
  placeholder?: string;
  isMulti?: IsMulti;
  sizeMulti?: 'sm' | 'md';
  withBorder?: boolean;
  classNameLabel?: string;
  classNameSelect?: string;
  autoHeight?: boolean;
  bgTransparent?: boolean;
  rounded?: 'full' | 'right' | 'left';
  debounceTimeoutSearch?: number;
  isClearable?: boolean;
  showBorderBottom?: boolean;
  fixedOption?: OptionValue<IsMulti, Option>;
  addedLastOption?: AddedLastOption;
  hintLabel?: string;
  formatCreateLabel?: string;
  showSelectedOptionsInMenu?: boolean;
  closeMenuOnSelect?: boolean;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
}

export const SelectPaginate = <T = string, Multiple extends boolean = false>({
  id,
  name,
  value,
  label,
  error,
  warning,
  onAsync,
  isMulti,
  isLoading,
  onChange,
  disabled,
  className,
  cacheUniqs,
  positionMenu,
  placeholder,
  defaultValue,
  fixedOption,
  sizeMulti = 'md',
  position = 'relative',
  withCreatable = false,
  withBorder = false,
  classNameLabel,
  classNameSelect,
  autoHeight = true,
  bgTransparent = false,
  showBorderBottom = true,
  rounded = 'full',
  debounceTimeoutSearch = 1000,
  isClearable = false,
  addedLastOption,
  hintLabel,
  formatCreateLabel,
  showSelectedOptionsInMenu = false,
  closeMenuOnSelect,
  onMenuOpen,
  onMenuClose,
}: CreatableSelectProps<T, Multiple>) => {
  const selectRef = useRef<any>(null);
  const [isPaginationLoading, setIsPaginationLoading] = useState(isLoading);

  const loadOptions = async (
    search: string,
    _: OptionValue<true, T>[], // prev async options
    additional: { page: number } | undefined,
  ): Promise<any> => {
    const page = additional?.page ?? 1;
    setIsPaginationLoading(true);
    try {
      const optionsNew = await onAsync(page, search ? search : '');
      return {
        options: fixedOption && page === 1 ? [fixedOption, ...optionsNew] : optionsNew,
        hasMore: Array.isArray(optionsNew) ? optionsNew.length >= 1 : false,
        additional: { page: page + 1 },
      };
    } finally {
      setIsPaginationLoading(false);
    }
  };

  const handleCreate = (inputValue: string) => {
    const newOption: any = createOption(inputValue);
    onChange &&
      onChange(!isMulti ? newOption : [...((value || []) as OptionValue<true, T>), newOption]);
  };

  const MenuList = useCallback(
    (props: MenuListProps) => {
      const { children, selectProps } = props;

      const handleMouseEnter = () => {
        (selectProps as any)?.selectRef?.current?.setState({
          focusedOption: null,
        });
      };

      return (
        <components.MenuList {...props}>
          {children}
          <div
            onMouseEnter={handleMouseEnter}
            className="px-3 py-2 text-blue-700 hover:bg-[#efefef] cursor-pointer border-t border-gray-200"
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              addedLastOption?.onClick && addedLastOption?.onClick();
            }}
          >
            {addedLastOption?.label ?? 'Add new'}
          </div>
        </components.MenuList>
      );
    },
    [addedLastOption],
  );

  const menuList = useMemo(
    () => (addedLastOption ? { MenuList: wrapMenuList(MenuList) } : {}),
    [MenuList, addedLastOption],
  );

  useEffect(() => {
    setIsPaginationLoading(isLoading);
  }, [isLoading]);

  const shouldShowSelectedOptionsInMenu = !!isMulti && showSelectedOptionsInMenu;

  const baseSelectProps: AsyncPaginateProps<
    any,
    GroupBase<OptionType>,
    { page: number },
    Multiple
  > & {
    blurInputOnSelect?: boolean;
    closeMenuOnSelect?: boolean;
    hideSelectedOptions?: boolean;
    showSelectedOptionsInMenu?: boolean;
  } = {
    id,
    name,
    value,
    isMulti,
    defaultValue,
    cacheUniqs,
    onChange: onChange as any,
    additional: { page: 1 },
    isDisabled: disabled,
    isClearable: isClearable,
    loadOptions: loadOptions as any,
    components: useComponents({
      Option,
      MultiValueLabel,
      ...menuList,
    } as any),
    getOptionValue: option => String(normalize(option.value)),
    loadOptionsOnMenuOpen: true,
    isLoading: isPaginationLoading,
    debounceTimeout: debounceTimeoutSearch,
    selectRef,
    reloadOnErrorTimeout: 5000,
    onMenuOpen,
    onMenuClose,
    blurInputOnSelect: shouldShowSelectedOptionsInMenu ? false : undefined,
    closeMenuOnSelect: closeMenuOnSelect ?? (shouldShowSelectedOptionsInMenu ? false : undefined),
    hideSelectedOptions: shouldShowSelectedOptionsInMenu ? false : undefined,
    showSelectedOptionsInMenu: shouldShowSelectedOptionsInMenu,
  };

  const CurrentSelect =
    withCreatable === true ? (
      <CreatableAsyncPaginate
        {...baseSelectProps}
        placeholder={placeholder || 'Select or write...'}
        formatCreateLabel={
          formatCreateLabel ? inputValue => `${formatCreateLabel} "${inputValue}"` : undefined
        }
        onCreateOption={handleCreate}
        styles={
          styles({
            positionMenu,
            creatable: true,
            position,
            sizeMulti,
            withBorder,
            autoHeight,
            bgTransparent,
            rounded,
            isError: !!error,
            isClearable,
            showBorderBottom,
            showSelectedOptionsInMenu: shouldShowSelectedOptionsInMenu,
          }) as any
        }
      />
    ) : (
      <AsyncPaginate
        {...baseSelectProps}
        placeholder={placeholder}
        styles={
          styles({
            disabled,
            positionMenu,
            position,
            sizeMulti,
            withBorder,
            autoHeight,
            bgTransparent,
            rounded,
            isClearable,
            isError: !!error,
            showBorderBottom,
            showSelectedOptionsInMenu: shouldShowSelectedOptionsInMenu,
          }) as any
        }
      />
    );
  return (
    <div
      className={classNames(
        'flex',
        {
          'flex-col': !className?.includes('flex-row'),
          'w-full': !className?.includes('w-'),
        },
        className,
      )}
    >
      {label && (
        <span className="flex gap-1 items-center mb-2">
          <span
            className={classNames(
              'uppercase text-[10px] leading-[18px] text-gray-900 opacity-64',
              classNameLabel,
            )}
          >
            {label}
          </span>
          {hintLabel && (
            <TooltipNew text={hintLabel}>
              <img alt="info" src={InfoCircleIcon} className="min-w-4 min-h-4 h-4 w-4" />
            </TooltipNew>
          )}
        </span>
      )}

      <span className={classNameSelect}>{CurrentSelect}</span>
      {error && <span className="mt-2 text-sm text-red-500">{error}</span>}
      {warning && (
        <div className="mt-2 flex gap-2 items-center">
          <img src={WarningYellowIcon} alt="warning" className="w-4 h-4 min-w-4 min-h-4" />
          <span className="text-sm text-[#FFA500]">{warning}</span>
        </div>
      )}
    </div>
  );
};
